/**
 * ProtectYourProbesPage — `/protect-your-probes`
 *
 * Reactive content built because Justin reported "I get a lot of inquiries
 * about leaving probes in / landscapers with 2\" grass." Real demand =
 * reactive build, not speculative. This is the URL he DMs anyone asking
 * "how do I keep these from getting nicked."
 *
 * Affiliate-revenue side: links to Carson 6" valve boxes + utility marking
 * flags with the `turfpulse0d-20` Amazon Associates tag.
 *
 * Voice: practical, landscaper-respectful, no hype. Diagrams via CSS only
 * (no image dependencies).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import { FaqJsonLd } from '../components/FaqJsonLd';
import { Button } from '../components/ui/button';
import {
  Shield, MapPin, Crosshair, ExternalLink, ArrowRight,
  CheckCircle2, AlertCircle,
} from 'lucide-react';

const URL = 'https://getturfpulse.com/protect-your-probes';

const AMZ_VALVE_BOX = 'https://www.amazon.com/s?k=carson+6+inch+round+valve+box&tag=turfpulse0d-20';
const AMZ_FLAGS = 'https://www.amazon.com/s?k=lawn+marking+flags&tag=turfpulse0d-20';

const FAQS = [
  {
    q: "Do I really need to leave the probes in the ground year-round?",
    a: "Yes — for two reasons. First, soil moisture data is most valuable over time (you're building a baseline of how your specific yard depletes, recovers, and responds to seasonal changes). Second, pulling and reinserting probes every season disturbs the soil-probe contact and shifts your readings by 3-8 percentage points. Set them once, leave them.",
  },
  {
    q: "Will a plastic valve box block the WiFi signal to the gateway?",
    a: "No. The Ecowitt WH51 uses 433 MHz radio (not 2.4 GHz WiFi), which passes through plastic and even thin soil cover without meaningful attenuation. Carson and NDS valve boxes are plastic by design. Range tests with the lid closed: full 5-bar signal at 30+ feet from the GW1100 gateway.",
  },
  {
    q: "What if my landscapers don't recognize the valve box as 'don't touch'?",
    a: "Add a small printed label: \"IRRIGATION SENSOR — DO NOT MOVE\" stuck to the lid. Most commercial landscape crews trained anywhere in the last 10 years respect anything in a valve box because it could be a wire splice, solenoid, or quick-connect. Plus, the lid is flush — a rotary mower goes right over it.",
  },
  {
    q: "Can I just bury the probe completely so nothing sticks up?",
    a: "No — the WH51 needs a few inches of clearance to transmit. The body and antenna must be above grade for radio line-of-sight to the gateway. The valve box approach keeps the body protected AND keeps the antenna exposed inside the box, with the plastic lid letting the 433 MHz signal through.",
  },
  {
    q: "What if I have many zones and don't want to install valve boxes everywhere?",
    a: "Tier 2 (marking flags) works for most residential setups where you control the mow schedule. If a contractor crew is doing your maintenance, the valve box is cheap insurance — at $8-12 each, the cost to protect a $30 probe is trivial. Even at 8 zones that's under $100 to bulletproof the whole install.",
  },
  {
    q: "Should I avoid certain spots in the lawn entirely?",
    a: "Avoid: low spots that pond water (skews readings high), high-traffic foot paths (compaction skews readings low), within 2 ft of any sprinkler head (direct spray hits the probe and gives false-wet readings), and within 1 ft of bed edges (root zone composition differs from open turf). Aim for: middle of the irrigated zone, normal-traffic turf, away from spray heads.",
  },
];

export default function ProtectYourProbesPage() {
  return (
    <div
      className="min-h-screen bg-gradient-to-b from-zinc-50 via-white to-emerald-50/30 dark:from-zinc-950 dark:via-zinc-950 dark:to-emerald-950/10"
      data-testid="protect-your-probes-page"
    >
      <SEO
        title="How to Protect Soil Moisture Probes from Landscapers (Ecowitt WH51 / WH52)"
        description="Three practical fixes for keeping Ecowitt WH51 / WH52 soil moisture probes safe from rotary mowers, weed-eaters, and landscape crews. Carson valve box recipe, marking flags, and strategic placement. No probe replacements required."
        canonical={URL}
      />
      <BreadcrumbJsonLd
        items={[
          { label: 'Home', path: '/' },
          { label: 'Protect Your Probes', path: '/protect-your-probes' },
        ]}
      />
      <FaqJsonLd faqs={FAQS} />

      {/* Hero */}
      <section className="border-b border-border bg-zinc-900 text-zinc-50 dark:bg-zinc-950" data-testid="protect-hero">
        <div className="max-w-3xl mx-auto px-5 py-14 sm:py-16">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs font-bold text-amber-300">
            <Shield className="w-3.5 h-3.5" /> Field-tested fix
          </div>
          <h1 className="mt-4 text-3xl sm:text-5xl font-black tracking-tight leading-[1.05]">
            How to keep your soil probes safe from{' '}
            <span className="text-amber-300">2" grass + landscape crews.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-zinc-300 max-w-2xl leading-relaxed">
            The WH51 antenna sits about 2" above grade once installed. Most
            residential rotary mowers run 2.5-3" and clear it fine — but
            commercial crews running tight 2" cuts (especially on St.
            Augustine, fescue, or scalped bermuda) will nick it eventually.
            Here are the 3 fixes that actually work in the field.
          </p>
          <p className="mt-3 text-sm text-zinc-400">
            All three are cheap. Tier 1 is what commercial landscape contractors use for their own soil sensors.
          </p>
        </div>
      </section>

      {/* Tier 1 — Valve Box */}
      <section className="py-12 sm:py-14 bg-white dark:bg-zinc-950 border-b border-border" data-testid="protect-tier-1">
        <div className="max-w-3xl mx-auto px-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="rounded-full bg-emerald-600 text-white text-xs font-bold w-7 h-7 flex items-center justify-center">🥇</span>
            <span className="text-xs uppercase tracking-widest font-bold text-emerald-700 dark:text-emerald-400">Tier 1 — Best Protection</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 dark:text-zinc-50">
            In-ground valve box (~$8-12 per probe)
          </h2>
          <p className="mt-2 text-sm sm:text-base text-zinc-700 dark:text-zinc-300 max-w-2xl leading-relaxed">
            Standard 6" round irrigation valve box. Drop it in flush with
            grade, probe sits inside protected, plastic lid lets the 433 MHz
            radio signal pass through. Crews see a "irrigation box" and leave it alone.
          </p>

          {/* CSS-only cross-section diagram */}
          <div className="mt-6 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 p-5">
            <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 dark:text-zinc-400 mb-3">
              Cross-section
            </p>
            <div className="relative h-40 max-w-md mx-auto" data-testid="protect-diagram">
              {/* Sky */}
              <div className="absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-sky-200 to-sky-100 dark:from-sky-900/30 dark:to-sky-950/20 rounded-t-md" />
              {/* Grass blades indicator */}
              <div className="absolute inset-x-0 top-5 h-2 bg-gradient-to-b from-emerald-400 to-emerald-600 dark:from-emerald-500 dark:to-emerald-700" />
              {/* Soil */}
              <div className="absolute inset-x-0 top-7 bottom-0 bg-gradient-to-b from-amber-800 to-amber-950 dark:from-amber-900 dark:to-amber-950 rounded-b-md" />
              {/* Valve box outline */}
              <div className="absolute left-1/2 -translate-x-1/2 top-6 w-28 h-28 border-4 border-zinc-200 dark:border-zinc-300 bg-zinc-100/40 dark:bg-zinc-200/10 rounded-b">
                {/* Box lid */}
                <div className="absolute -top-1.5 left-0 right-0 h-1.5 bg-zinc-300 dark:bg-zinc-400 rounded-t shadow-sm" />
                {/* Probe representation */}
                <div className="absolute left-1/2 -translate-x-1/2 top-1 w-1.5 h-16 bg-emerald-500 rounded-full" />
                <div className="absolute left-1/2 -translate-x-1/2 top-14 w-3 h-3 bg-emerald-700 rounded-full" />
              </div>
              <span className="absolute right-2 top-1 text-[10px] font-bold text-sky-700 dark:text-sky-300">grade</span>
              <span className="absolute right-2 top-12 text-[10px] font-bold text-emerald-700 dark:text-emerald-400">WH51 probe</span>
              <span className="absolute right-2 bottom-2 text-[10px] font-bold text-amber-700 dark:text-amber-300">root zone</span>
            </div>
            <p className="text-xs text-muted-foreground mt-3 text-center">
              Probe sits inside the box at root-zone depth. Lid flush with grade. Mower passes over the lid harmlessly.
            </p>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            <div className="rounded-md border border-emerald-300/60 dark:border-emerald-800/60 bg-emerald-50/40 dark:bg-emerald-950/20 p-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <h3 className="font-bold text-sm text-emerald-900 dark:text-emerald-200">Why it works</h3>
              </div>
              <ul className="space-y-1 text-xs text-zinc-800 dark:text-zinc-200">
                <li>• Plastic lid → no signal attenuation</li>
                <li>• Flush with grade → mowers safe</li>
                <li>• Universal "do not touch" visual</li>
                <li>• Easy probe access for battery swaps</li>
                <li>• Standard hardware-store part</li>
              </ul>
            </div>
            <div className="rounded-md border border-amber-300/60 dark:border-amber-800/60 bg-amber-50/40 dark:bg-amber-950/20 p-4">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                <h3 className="font-bold text-sm text-amber-900 dark:text-amber-200">Heads-up</h3>
              </div>
              <ul className="space-y-1 text-xs text-zinc-800 dark:text-zinc-200">
                <li>• Use the 6" ROUND size (not the rectangular)</li>
                <li>• Set lid level — bumps = mower scalps</li>
                <li>• Cut sod with a flat shovel, save the plug</li>
                <li>• 15-min install per probe</li>
              </ul>
            </div>
          </div>

          <a
            href={AMZ_VALVE_BOX}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-block"
            data-testid="protect-amzn-valvebox"
          >
            <Button size="lg" className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold">
              Carson 6" round valve box on Amazon
              <ExternalLink className="ml-1.5 w-4 h-4" />
            </Button>
          </a>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Affiliate link — small commission if you buy, same price for you.
          </p>
        </div>
      </section>

      {/* Tier 2 — Flags */}
      <section className="py-12 sm:py-14 bg-zinc-50/60 dark:bg-zinc-900/30 border-b border-border" data-testid="protect-tier-2">
        <div className="max-w-3xl mx-auto px-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="rounded-full bg-amber-500 text-white text-xs font-bold w-7 h-7 flex items-center justify-center">🥈</span>
            <span className="text-xs uppercase tracking-widest font-bold text-amber-700 dark:text-amber-400">Tier 2 — Cheap & Effective</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 dark:text-zinc-50">
            Utility marking flags (~$5 for 25)
          </h2>
          <p className="mt-2 text-sm sm:text-base text-zinc-700 dark:text-zinc-300 max-w-2xl leading-relaxed">
            Bright orange or yellow utility flags — the kind your locator uses
            before digging. Stab one 4" from each probe. Universal "this is
            something, don't run it over" signal. Won't survive a string
            trimmer hitting it directly, but rotary mowers respect them.
          </p>

          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <div className="rounded-md border border-emerald-300/60 dark:border-emerald-800/60 bg-emerald-50/40 dark:bg-emerald-950/20 p-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <h3 className="font-bold text-sm text-emerald-900 dark:text-emerald-200">Best for</h3>
              </div>
              <ul className="space-y-1 text-xs text-zinc-800 dark:text-zinc-200">
                <li>• You mow your own lawn</li>
                <li>• Single trusted crew you can train</li>
                <li>• Trial / pilot deployment</li>
                <li>• Cost-conscious starter setup</li>
              </ul>
            </div>
            <div className="rounded-md border border-amber-300/60 dark:border-amber-800/60 bg-amber-50/40 dark:bg-amber-950/20 p-4">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                <h3 className="font-bold text-sm text-amber-900 dark:text-amber-200">Skip if</h3>
              </div>
              <ul className="space-y-1 text-xs text-zinc-800 dark:text-zinc-200">
                <li>• Rotating crew you don't know</li>
                <li>• Property gets aggressive weed-eating</li>
                <li>• Snowbird / vacant rental property</li>
                <li>• Kids/pets that pull flags out</li>
              </ul>
            </div>
          </div>

          <a
            href={AMZ_FLAGS}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-block"
            data-testid="protect-amzn-flags"
          >
            <Button size="lg" variant="outline" className="border-amber-400 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30 font-bold">
              Utility marking flags on Amazon
              <ExternalLink className="ml-1.5 w-4 h-4" />
            </Button>
          </a>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Affiliate link — small commission if you buy, same price for you.
          </p>
        </div>
      </section>

      {/* Tier 3 — Placement */}
      <section className="py-12 sm:py-14 bg-white dark:bg-zinc-950 border-b border-border" data-testid="protect-tier-3">
        <div className="max-w-3xl mx-auto px-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="rounded-full bg-zinc-500 text-white text-xs font-bold w-7 h-7 flex items-center justify-center">🥉</span>
            <span className="text-xs uppercase tracking-widest font-bold text-zinc-600 dark:text-zinc-400">Tier 3 — Free, with tradeoff</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 dark:text-zinc-50">
            Strategic placement (free)
          </h2>
          <p className="mt-2 text-sm sm:text-base text-zinc-700 dark:text-zinc-300 max-w-2xl leading-relaxed">
            Put the probes where mowers don't go aggressively. Honest
            tradeoff: protected location ≠ representative location, so your
            readings may not reflect the whole zone perfectly.
          </p>

          <div className="mt-6 grid sm:grid-cols-3 gap-3">
            <div className="rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
              <Crosshair className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <h3 className="mt-2 font-bold text-sm text-zinc-900 dark:text-zinc-50">Near rotor heads</h3>
              <p className="mt-1 text-xs text-zinc-700 dark:text-zinc-300">
                Already a "no-mow" radius. Place 6-8" from the head, downhill of the spray.
              </p>
            </div>
            <div className="rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
              <MapPin className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <h3 className="mt-2 font-bold text-sm text-zinc-900 dark:text-zinc-50">6-8" inside bed edges</h3>
              <p className="mt-1 text-xs text-zinc-700 dark:text-zinc-300">
                Mowers slow at the edge. Still in turf root zone — barely.
              </p>
            </div>
            <div className="rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
              <Shield className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <h3 className="mt-2 font-bold text-sm text-zinc-900 dark:text-zinc-50">No-mow zones</h3>
              <p className="mt-1 text-xs text-zinc-700 dark:text-zinc-300">
                Tree wells, ornamental islands, drip-irrigated beds — if any zone has them.
              </p>
            </div>
          </div>

          <div className="mt-5 rounded-md border-l-4 border-l-amber-500 bg-amber-50/40 dark:bg-amber-950/20 p-4">
            <p className="text-sm text-amber-900 dark:text-amber-200">
              <strong>The tradeoff to know:</strong> A probe near a rotor head reads slightly wetter on fire days, slightly drier between cycles, compared to mid-zone. Calibrate your trigger thresholds accordingly — typically add 3-5 percentage points to the soil-moisture floor.
            </p>
          </div>
        </div>
      </section>

      {/* Honest "what I do" */}
      <section className="py-12 sm:py-14 bg-emerald-50/40 dark:bg-emerald-950/20 border-b border-border" data-testid="protect-what-i-do">
        <div className="max-w-3xl mx-auto px-5">
          <h2 className="text-xl sm:text-2xl font-black tracking-tight text-zinc-900 dark:text-zinc-50">
            What I (Justin) do on my own lawn
          </h2>
          <p className="mt-3 text-sm sm:text-base text-zinc-700 dark:text-zinc-300 leading-relaxed">
            I leave my probes installed year-round and I know where they are.
            I mow my own lawn at 1.25-1.5" (low for bermuda) so I just
            mentally route around them. If you're in the same boat — solo
            owner-operator, you mow yourself — you may not need any of these
            fixes at all. The valve box is for when the situation changes:
            you hire a crew, you sell the house and the new owners don't
            know where the probes are, or you're managing more than one property.
          </p>
          <p className="mt-3 text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
            For anyone with a landscape contractor on the property, my
            standing recommendation is the Carson box from day one. $8 of
            cheap insurance against a $30 probe replacement plus the data
            gap while it's offline.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-14 bg-white dark:bg-zinc-950 border-b border-border" data-testid="protect-faq">
        <div className="max-w-3xl mx-auto px-5">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 dark:text-zinc-50 mb-6">
            Common questions
          </h2>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <details
                key={i}
                className="group rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50/60 dark:bg-zinc-900/60 p-4"
                data-testid={`protect-faq-${i}`}
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-3 font-semibold text-zinc-900 dark:text-zinc-50 text-sm sm:text-base">
                  <span>{f.q}</span>
                  <span className="text-emerald-600 dark:text-emerald-400 group-open:rotate-45 transition-transform shrink-0">+</span>
                </summary>
                <p className="mt-3 text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-zinc-900 text-zinc-50" data-testid="protect-cta">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight">
            Got probes installed? See the full Ecowitt → Rachio loop.
          </h2>
          <p className="mt-3 text-sm text-zinc-300 max-w-2xl mx-auto">
            Once your probes are physically safe, the next step is making them
            actually drive your irrigation. Here's how to wire them into Rachio
            so your controller fires only when the soil agrees.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/ecowitt-to-rachio">
              <Button className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold" data-testid="protect-cta-primary">
                See the Ecowitt → Rachio breakdown <ArrowRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/help-me-connect-ecowitt">
              <Button variant="outline" className="border-zinc-600 text-zinc-200 hover:bg-zinc-800">
                The 5-min summary
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
